import { z } from "zod";

import { isDevelopment } from "@av/build-constants";
import type { FormExternalError } from "@av/components/atoms/form/types";
import {
  type ApiErrorResponse,
  getDisplayedError,
  ValidationErrorSchema,
} from "./error-i18n-mappings";

import type { AxiosError } from "axios";
import type { TFunction } from "i18next";

/**
 * Maps API validation errors from an Axios error response to form server errors.
 *
 * @param axiosError - The {@link AxiosError} containing {@link ApiErrorResponse} with validation errors from the API
 * @returns Array of {@link FormExternalError} ready for display in form fields
 */
export function mapErrors(
  t: TFunction,
  axiosError?: AxiosError<ApiErrorResponse> | null
): FormExternalError[] {
  if (axiosError == null) {
    return [];
  }

  const validationErrors = axiosError?.response?.data?.validationErrors;

  if (!validationErrors) {
    return [];
  }

  const typeCheck = z.array(ValidationErrorSchema).safeParse(validationErrors);

  if (!typeCheck.success) {
    if (isDevelopment) {
      console.warn("Server returned an unrecognized error object: ", axiosError?.response?.data, {
        typeCheck,
      });

      throw Error("Server returned an unrecognized validationErrors object");
    }

    return [];
  }

  return validationErrors.map((e) => ({
    field: e.field,
    error: {
      ...getDisplayedError(t, e.code, e.params),
      code: e.code,
    },
  }));
}

const indexedListItemSubFieldPattern = /^\[(\d+)\]\.[A-Za-z0-9_]+$/;

/**
 * The API validates each item of an indexed list by sub-field, e.g.
 * "screeningQuestions[1].version" or "screeningAnswers[1].actualBooleanAnswer".
 * A form list usually renders a single visible, labeled control per item, and the
 * form tracks/clears list errors by dot-notation paths. So point every per-item
 * sub-field error at that item's visible control, convert the API's bracket
 * notation to the dot notation the form uses, and collapse the duplicate
 * sub-field errors into a single error per item.
 *
 * Example: with parentField "screeningQuestions" and visibleSubField
 * "screeningQuestionId", both "screeningQuestions[1].version" and
 * "screeningQuestions[1].screeningQuestionId" become one error on
 * "screeningQuestions.1.screeningQuestionId".
 */
export function mapIndexedListServerErrors(
  serverErrors: FormExternalError[],
  parentField: string,
  visibleSubField: string
): FormExternalError[] {
  const mappedFields = new Set<string>();

  return serverErrors.flatMap((serverError) => {
    if (!serverError.field.startsWith(parentField)) {
      return [serverError];
    }

    const match = serverError.field.slice(parentField.length).match(indexedListItemSubFieldPattern);

    if (!match) {
      return [serverError];
    }

    const [, index] = match;
    const field = `${parentField}.${index}.${visibleSubField}`;

    if (mappedFields.has(field)) {
      return [];
    }

    mappedFields.add(field);

    return [{ ...serverError, field }];
  });
}

/**
 * Checks if a validation error code exists on the API error response.
 */
export function hasError(
  axiosError: AxiosError<ApiErrorResponse> | null | undefined,
  validationErrorCode: string
): boolean {
  const validationErrors = axiosError?.response?.data?.validationErrors;

  if (!validationErrors) {
    return false;
  }

  return validationErrors.some(({ code }) => code === validationErrorCode);
}
